import { inject, Injectable } from '@angular/core';
import { map, Observable, withLatestFrom } from 'rxjs';
import { IDENTITY_PROVIDER } from '@app/core/config/identity-provider';
import { ApiService } from './api.service';
import { Store } from '@ngrx/store';
import { getLoggedInUser } from '@app/collections/users/users.selectors';
import { Pagination } from '../models/pagination';
import { User } from '../models/user';
import { Tag } from '../mojo-tag/mojo-tag.model';

@Injectable({
  providedIn: 'root',
})
export class UserService extends ApiService {
  private store = inject(Store);
  currentUser$ = this.store.select(getLoggedInUser);

  constructor() {
    super();
    this.url = 'users';
  }

  autocomplete(search: string): Observable<Tag[]> {
    return this.getUsers(search).pipe(
      map((page) => page.content),
      withLatestFrom(this.currentUser$),
      map(([users, currentUser]) =>
        users
          .filter((user: User) => currentUser !== null && user.id !== currentUser.id)
          .map((user: User) => ({
            id: user?.id,
            label: user?.firstName + ' ' + user?.lastName,
            initials: user?.firstName[0] + user?.lastName[0],
            imageId: user?.photoId,
          })),
      ),
    );
  }

  getUsers(search = '', page = 0, size = 15): Observable<Pagination<User>> {
    return this.get({ params: { search, page, size } });
  }

  getUsersByIds(ids: number[]): Observable<User[]> {
    return this.post({ url: '/by-ids', body: ids });
  }

  updateFirstAndLastNames(firstName: string, lastName: string, id?: number): Observable<User> {
    const user = {
      id,
      firstName,
      lastName,
    } as User;
    return this.patch<{ firstName: string; lastName: string }, User>({
      url: '/' + id,
      body: user,
    }).pipe(map((user) => user));
  }

  changePassword(newPassword: string, currentPassword: string): Observable<User> {
    return this.patch<{ newPassword: string; currentPassword: string }, User>({
      url: '/change-password',
      params: IDENTITY_PROVIDER === 'keycloak' ? { keycloak: true } : {},
      body: { newPassword, currentPassword },
    }).pipe(map((user) => user));
  }

  changeConnectedToGraph(userId: number, connectedToGraph: boolean): Observable<User> {
    return this.patch({ url: `/${userId}?connectedToGraph=${connectedToGraph}` });
  }

  changeConnectedToGmail(userId: number, connectedToGmail: boolean): Observable<User> {
    return this.patch({ url: `/${userId}?connectedToGmail=${connectedToGmail}` });
  }

  checkIfGivenEmailsAreAlreadyTaken(emails: string[]): Observable<User[]> {
    return this.post({ url: '/emails', body: emails });
  }
}
